<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container whats-new-modal">
      <div class="modal-header">
        <div class="header-content">
          <i class="fas fa-gift header-icon"></i>
          <div>
            <h2>What's New</h2>
            <p class="subtitle">Latest updates and improvements</p>
          </div>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div v-if="releaseNotes.length === 0" class="empty-state">
          <i class="fas fa-check-circle"></i>
          <p>You're all caught up! No new updates.</p>
        </div>
        
        <div v-else class="release-list">
          <div 
            v-for="(release, index) in releaseNotes" 
            :key="release.version"
            class="release-card"
            :class="{ 'latest': index === 0 }"
          >
            <div class="release-header">
              <div class="version-tag">
                <i class="fas fa-tag"></i>
                v{{ release.version }}
              </div>
              <div class="release-date">{{ formatDate(release.date) }}</div>
            </div>
            
            <h3 class="release-title">{{ release.title }}</h3>
            
            <!-- Highlights -->
            <div v-if="release.highlights?.length" class="highlights">
              <div 
                v-for="(highlight, hIndex) in release.highlights" 
                :key="hIndex"
                class="highlight-item"
              >
                <i class="fas fa-star"></i>
                <span>{{ highlight }}</span>
              </div>
            </div>
            
            <!-- Testing Instructions (shown prominently if present) -->
            <div v-if="release.testing_instructions?.length" class="testing-section">
              <div class="testing-header">
                <i class="fas fa-clipboard-check"></i>
                <span>Testing Instructions</span>
                <span class="test-count">{{ release.testing_instructions.length }} steps</span>
              </div>
              <div class="testing-steps">
                <div 
                  v-for="instruction in release.testing_instructions" 
                  :key="instruction.id"
                  class="test-step"
                >
                  <div class="step-number">{{ instruction.step_number }}</div>
                  <div class="step-content">
                    <div class="step-description">{{ instruction.description }}</div>
                    <div v-if="instruction.expected_result" class="step-expected">
                      <i class="fas fa-check-circle"></i>
                      <span>Expected: {{ instruction.expected_result }}</span>
                    </div>
                    <span v-if="instruction.area" class="area-badge" :class="instruction.area">
                      {{ instruction.area_display || instruction.area }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Detailed Categories (expandable) -->
            <details v-if="hasDetails(release)" class="details-section">
              <summary>
                <i class="fas fa-list"></i>
                View all changes
              </summary>
              
              <div class="categories">
                <!-- Features -->
                <div v-if="release.categories?.features?.length" class="category">
                  <h4><i class="fas fa-plus-circle"></i> New Features</h4>
                  <ul>
                    <li v-for="(item, i) in release.categories.features" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                
                <!-- Improvements -->
                <div v-if="release.categories?.improvements?.length" class="category">
                  <h4><i class="fas fa-arrow-up"></i> Improvements</h4>
                  <ul>
                    <li v-for="(item, i) in release.categories.improvements" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                
                <!-- Fixes -->
                <div v-if="release.categories?.fixes?.length" class="category">
                  <h4><i class="fas fa-wrench"></i> Bug Fixes</h4>
                  <ul>
                    <li v-for="(item, i) in release.categories.fixes" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn btn-primary" @click="$emit('close')">
          Got it!
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WhatsNewModal',
  
  props: {
    releaseNotes: {
      type: Array,
      default: () => [],
    },
  },
  
  emits: ['close'],
  
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    
    hasDetails(release) {
      const cats = release.categories || {};
      return (cats.features?.length || 0) + 
             (cats.improvements?.length || 0) + 
             (cats.fixes?.length || 0) > 0;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.whats-new-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  font-size: 32px;
  opacity: 0.9;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.modal-header .subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  opacity: 0.9;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #718096;
}

.empty-state i {
  font-size: 48px;
  color: #68d391;
  margin-bottom: 16px;
}

.release-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.release-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.release-card.latest {
  background: linear-gradient(135deg, #fef3cd 0%, #fff3e0 100%);
  border-color: #f6ad55;
}

.release-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.version-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #667eea;
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.release-card.latest .version-tag {
  background: #f6ad55;
  color: #744210;
}

.release-date {
  font-size: 12px;
  color: #718096;
}

.release-title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.highlights {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.highlight-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #4a5568;
}

.highlight-item i {
  color: #f6ad55;
  margin-top: 2px;
  font-size: 10px;
}

/* Testing Instructions Section */
.testing-section {
  margin-top: 16px;
  background: linear-gradient(135deg, #e6fffa 0%, #ebf8ff 100%);
  border: 1px solid #38b2ac;
  border-radius: 8px;
  padding: 12px;
}

.testing-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #234e52;
  margin-bottom: 12px;
}

.testing-header i {
  color: #38b2ac;
}

.test-count {
  margin-left: auto;
  background: #38b2ac;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.testing-steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.test-step {
  display: flex;
  gap: 10px;
  background: white;
  border-radius: 6px;
  padding: 10px;
  border: 1px solid #e2e8f0;
}

.step-number {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background: #38b2ac;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.step-content {
  flex: 1;
}

.step-description {
  font-size: 13px;
  color: #2d3748;
  margin-bottom: 6px;
}

.step-expected {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #38a169;
  margin-bottom: 6px;
}

.step-expected i {
  margin-top: 2px;
  font-size: 10px;
}

.area-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
}

.area-badge.frontend {
  background: #ebf4ff;
  color: #3182ce;
}

.area-badge.backend {
  background: #faf5ff;
  color: #805ad5;
}

.area-badge.api {
  background: #fffaf0;
  color: #dd6b20;
}

.area-badge.database {
  background: #f0fff4;
  color: #38a169;
}

.area-badge.integration {
  background: #fff5f5;
  color: #e53e3e;
}

.area-badge.general {
  background: #f7fafc;
  color: #718096;
}

.details-section {
  margin-top: 12px;
  border-top: 1px solid #e2e8f0;
  padding-top: 12px;
}

.details-section summary {
  cursor: pointer;
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.details-section summary:hover {
  color: #5a67d8;
}

.categories {
  margin-top: 12px;
}

.category {
  margin-bottom: 12px;
}

.category h4 {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 6px;
}

.category h4 i {
  font-size: 11px;
}

.category ul {
  margin: 0;
  padding-left: 24px;
}

.category li {
  font-size: 12px;
  color: #718096;
  margin-bottom: 4px;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #667eea;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #5a67d8;
}
</style>
